import { createContext, useState, useEffect, useCallback } from 'react';
import type { Theme, ResolvedTheme, ThemeContextValue, ThemeProviderProps } from './types';
import logger from '../utils/logger';

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * ThemeProvider - Manages theme state and provides theme context
 *
 * Supports light, dark, and auto (system preference) modes.
 * Persists user preference to localStorage and syncs across tabs.
 * Prevents flash on page load with inline script in index.html.
 *
 * @example
 * <ThemeProvider defaultTheme="auto">
 *   <App />
 * </ThemeProvider>
 */
export function ThemeProvider({
  children,
  defaultTheme = 'auto',
  storageKey = 'theme-preference',
}: ThemeProviderProps) {
  // Initialize from localStorage
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return defaultTheme;

    try {
      const stored = localStorage.getItem(storageKey) as Theme | null;
      if (stored && ['light', 'dark', 'auto'].includes(stored)) {
        return stored;
      }
    } catch (error) {
      logger.error('ThemeProvider', 'Failed to read theme from localStorage:', error);
    }
    return defaultTheme;
  });

  // Resolve 'auto' to actual light/dark
  const [effectiveTheme, setEffectiveTheme] = useState<ResolvedTheme>(() => {
    if (typeof window === 'undefined') return 'light';

    if (theme !== 'auto') return theme as ResolvedTheme;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Apply theme to DOM
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', effectiveTheme === 'dark');
    root.style.colorScheme = effectiveTheme;
  }, [effectiveTheme]);

  // Listen to system preference changes when theme='auto'
  useEffect(() => {
    if (theme !== 'auto') {
      setEffectiveTheme(theme as ResolvedTheme);
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setEffectiveTheme(e.matches ? 'dark' : 'light');
    };

    // Set initial value
    setEffectiveTheme(mediaQuery.matches ? 'dark' : 'light');

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Persist to localStorage
  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeState(newTheme);
      try {
        localStorage.setItem(storageKey, newTheme);
      } catch (error) {
        logger.error('ThemeProvider', 'Failed to save theme to localStorage:', error);
      }
    },
    [storageKey]
  );

  // Toggle between light/dark
  const toggleTheme = useCallback(() => {
    setTheme(effectiveTheme === 'light' ? 'dark' : 'light');
  }, [effectiveTheme, setTheme]);

  // Sync across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey && e.newValue) {
        const newTheme = e.newValue as Theme;
        if (['light', 'dark', 'auto'].includes(newTheme)) {
          setThemeState(newTheme);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [storageKey]);

  return (
    <ThemeContext.Provider value={{ theme, effectiveTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export { ThemeContext };
